import {
  AnalyticsEvents,
  setCreateDropdownMenu,
  setIsOverlayLogin,
  setSideNavigationMenu,
  SubscriptionTiers,
  useApp,
  useAppDispatch,
  useAppSelector,
  UserRoles,
  useTranslation,
  useUserInfo,
} from "@next-platform/client-common-core";
import {
  CapabilityKey,
  SubCapabilityKey,
  useApplicationCapabilitiesAllowed,
  useApplicationCapabilityReady,
} from "@next-platform/client-common-core/infra/application/capabilities";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { BadgeColorVariant, ButtonVariant, IconVariants, Navigation as NavigationUI } from "@next-platform/client-common-spicy-ui";
import localSession from "core-js/internals/task.js";
import { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { SiteLogo } from "@/components";
import { DiscordThemeIcon, RedditThemeIcon, TwitterThemeIcon } from "@/components/icons";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import {
  APPLICATIONS_ID,
  CREATE_DROPDOWN_LOCAL_STORAGE_KEY,
  DOMAINS,
  EXTERNAL_ROUTES_FOR_APPLICATION,
  FEATURE_FLAGS,
  ROUTES,
  SIDE_NAVIGATION_LOCAL_STORAGE_KEY,
  SOCIAL_MEDIA_EVENT_VALUE,
  SPICYCHAT_DISCORD,
  SPICYCHAT_REDDIT,
  SPICYCHAT_TWITTER,
} from "@/constants";
import useNavigationApplicationBadges from "@/features/mobileApplications/useNavigationApplicationBadges";
import { useBrand, useFeatureFlag, useHasOtherCreationFeatures, useMediaStore, useNavigation, useTheme } from "@/hooks";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import { useLocalizedPathname } from "@/hooks/useLocalizedPathname";
import useQueueLogEvent from "@/hooks/useQueueLogEvent";
import UnlockCustomVoicesModal from "@/pages/CreateCharacter/v2/components/UnlockCustomVoicesModal";
import useAuth from "@/services/useAuth";
import { getBusinessRouteScreen } from "@/utils/getBusinessRouteScreen";
import { getIsPartner } from "@/utils/getIsPartner";
import { typedLocalStorageService } from "@/utils/localStorageRe";

import { OverlayLogin } from "../overlays";

function Navigation() {
  const { t } = useTranslation();
  const { isSpicyChat } = useBrand();
  const applicationId = basicAppConfig.applicationId;
  const { logEvent } = useQueueLogEvent();
  const isMobile = useMediaStore((state) => state.isMobile);
  const isDesktop = useMediaStore((state) => state.isDesktop);
  const { isDark } = useTheme();
  const { checkRoute, handleSideNavClose, isSideNavOpen, handleSideNavToggle, isCreateDropdownOpen, handleCreateDropdownToggle } = useNavigation();
  const { isAuthenticated: isUserAuthenticated, logout } = useAuth();
  ///this state should live in business-logic, which is impossible at the moment due to a breaking change over there
  /// to be fixed after next release
  const [isCreationsOpen, setIsCreationsOpen] = useState(true);
  const navigate = useNavigate();
  const currentRoute = useLocalizedLocation()?.pagePath;
  const screenName = getBusinessRouteScreen(currentRoute);
  const dispatch = useAppDispatch();
  const { isProfileModified, username, isAuthenticated: isKindeAuthenticated, isAuthLoaded, role } = useUserInfo();
  const [stateIsUnlockVoicesModalOpen, setStateIsUnlockVoicesModalOpen] = useState(false);
  const { logoutDomain = DOMAINS.SPICYCHAT, branding, applicationName } = useApp();
  const { customerSupportUrl, privacyPolicyUrl, refundPolicyUrl, tcUrl } = branding || {};
  const localizePathname = useLocalizedPathname();

  const isPartner = getIsPartner();
  const isOverlayLogin = useAppSelector((state) => state?.navigation?.isOverlayLogin);
  const { hasFeatureFlag: hasLorebookDiscoverPublic } = useFeatureFlag(FEATURE_FLAGS.LOREBOOK_DISCOVER_PUBLIC);
  const { hasFeatureFlag: hasHideSidebarPagesFlag } = useFeatureFlag(FEATURE_FLAGS.HIDE_SIDEBAR_PAGES);
  const { hasVoice, hasAnyCreationFeatures } = useHasOtherCreationFeatures();
  const [
    isLorebookPublicDiscoveryAllowed,
    isLorebookManageAllowed,
    isGroupChatManageAllowed,
    isTtsManageCustomVoiceAllowed,
    isBlockCreatorsAllowed,
    isRulebookManageAllowed,
    isRulebookPublicDiscoveryAllowed,
  ] = useApplicationCapabilitiesAllowed([
    SubCapabilityKey.LorebookPublicDiscovery,
    SubCapabilityKey.LorebookManage,
    SubCapabilityKey.GroupChatManage,
    SubCapabilityKey.TtsManageCustomVoice,
    SubCapabilityKey.DiscoverBlockCreators,
    SubCapabilityKey.RulebookManage,
    SubCapabilityKey.RulebookPublicDiscovery,
  ]);
  const isCapabilityReady = useApplicationCapabilityReady();
  const isDiscoveryPageAllowed = useApplicationCapabilitiesAllowed(CapabilityKey.DiscoveryPage);
  const isGalleryAllowed = useApplicationCapabilitiesAllowed(SubCapabilityKey.GalleryMyGallery);
  const isAuthenticated = !!(username || (isPartner && isUserAuthenticated));
  const isAnonymousOrFreeUser = !isAuthenticated || role === UserRoles.FREE;
  const isHiddenForSignedOut = isAuthLoaded && !isKindeAuthenticated && hasHideSidebarPagesFlag;
  const applicationBadges = useNavigationApplicationBadges({
    applicationName,
    onPress: handleSideNavClose,
  });

  useEffect(() => {
    const localStorageSideNavValue = typedLocalStorageService.getItem(SIDE_NAVIGATION_LOCAL_STORAGE_KEY);
    // Keep sidebar closed on tablet and mobile, open on desktop and wider screens after refresh
    const isSideNavOpenByDefault = isDesktop && (localStorageSideNavValue === null || localStorageSideNavValue === "true");
    dispatch(setSideNavigationMenu(isSideNavOpenByDefault));
  }, []);

  useEffect(() => {
    if (hasAnyCreationFeatures) {
      const localStorageCreateDropdownValue = typedLocalStorageService.getItem(CREATE_DROPDOWN_LOCAL_STORAGE_KEY);
      const isCreateDropdownOpenByDefault = !isMobile && localStorageCreateDropdownValue === "true";
      dispatch(setCreateDropdownMenu(isCreateDropdownOpenByDefault));
    }
  }, [hasAnyCreationFeatures, hasVoice, isMobile, dispatch]);

  const onClickLogin = () => {
    dispatch(setIsOverlayLogin(true));
    handleSideNavClose();
  };

  const onClickLogout = async () => {
    handleSideNavClose();
    await logout();
    localSession.clear();

    if (logoutDomain && isPartner) {
      window.open(`https://${logoutDomain}`, "_self");
    } else {
      navigate("/");
    }
  };

  const onOverlayLoginClose = useCallback(() => {
    dispatch(setIsOverlayLogin(false));
  }, [dispatch]);

  const isActiveRoute = (route: string) => {
    return currentRoute === route;
  };

  const handleLogoClick = useCallback((e: MouseEvent<HTMLAnchorElement>) => checkRoute(e, "/"), [checkRoute]);

  const handleNavigationClick = useMemo(
    () => (e: MouseEvent<HTMLButtonElement>, route: string) => {
      if (isProfileModified) {
        checkRoute(e, localizePathname(route));
      }
      if (isMobile) {
        handleSideNavClose();
      }
    },
    [isProfileModified, checkRoute, navigate, localizePathname],
  );

  const handleSocialMediaClick = (socialPlatform: string) => {
    handleSideNavClose();
    logEvent(AnalyticsEvents.SOCIAL_MEDIA_CLICK, {
      platform: socialPlatform,
      page_name: screenName,
    });
  };

  const handleCreationOpen = () => {
    setIsCreationsOpen((prev: boolean) => !prev);
  };

  const menuItems = useMemo(() => {
    const subscribeItem = {
      icon: IconVariants.CROWN,
      label: t("appMenu:subscribe"),
      hidden: !isAuthenticated,
      onClick: (e: MouseEvent<HTMLButtonElement>) => handleNavigationClick(e, ROUTES.subscribe),
      active: isActiveRoute(ROUTES.subscribe),
      to: localizePathname(ROUTES.subscribe),
    };

    return [
      [
        {
          icon: IconVariants.HOME,
          label: t("appMenu:home"),
          onClick: (e: MouseEvent<HTMLButtonElement>) => handleNavigationClick(e, ROUTES.home),
          active: isActiveRoute(ROUTES.home),
          to: localizePathname(ROUTES.home),
        },
        ...(isCapabilityReady && isDiscoveryPageAllowed
          ? [
              {
                icon: IconVariants.SPARKLES,
                label: t("appMenu:discovery"),
                onClick: (e: MouseEvent<HTMLButtonElement>) => handleNavigationClick(e, ROUTES.discover),
                active: isActiveRoute(ROUTES.discover),
                to: localizePathname(ROUTES.discover),
                badge: {
                  txt: t("appMenu:badges.new"),
                },
                "data-testid": "nav-discover",
              },
            ]
          : []),
        {
          icon: IconVariants.MESSAGE,
          label: t("appMenu:chats"),
          onClick: (e: MouseEvent<HTMLButtonElement>) => handleNavigationClick(e, ROUTES.chats),
          active: isActiveRoute(ROUTES.chats),
          to: localizePathname(ROUTES.chats),
        },
        {
          icon: IconVariants.IMAGES,
          // The nav reads "Gallery"; the page keeps its fuller "My Gallery" title.
          label: t("appMenu:gallery"),
          onClick: (e: MouseEvent<HTMLButtonElement>) => handleNavigationClick(e, ROUTES.myGallery),
          active: isActiveRoute(ROUTES.myGallery),
          to: localizePathname(ROUTES.myGallery),
          badge: {
            txt: t("appMenu:badges.new"),
          },
          "data-testid": "nav-my-gallery",
          hidden: !isCapabilityReady || !isGalleryAllowed || isHiddenForSignedOut,
        },
        {
          icon: IconVariants.BOOK_TEXT,
          label: t("appMenu:chatbots.lorebooks"),
          onClick: (e: MouseEvent<HTMLButtonElement>) => handleNavigationClick(e, ROUTES.lorebooksExplore),
          active: isActiveRoute(ROUTES.lorebooksExplore),
          to: localizePathname(ROUTES.lorebooksExplore),
          badge: {
            txt: t("appMenu:badges.new"),
          },
          "data-testid": "nav-lorebooks",
          // Discovery flag gates everyone (incl. anonymous and while it loads); signed-in users additionally need the capability, using the same auth signal as CapabilityRouteGuard.
          hidden: !hasLorebookDiscoverPublic || (isAuthLoaded && isKindeAuthenticated && !isLorebookPublicDiscoveryAllowed) || isHiddenForSignedOut,
        },
        {
          icon: IconVariants.SLIDER_HORIZONTAL,
          label: t("appMenu:chatbots.rulebooks"),
          onClick: (e: MouseEvent<HTMLButtonElement>) => handleNavigationClick(e, ROUTES.rulebooksExplore),
          active: isActiveRoute(ROUTES.rulebooksExplore),
          to: localizePathname(ROUTES.rulebooksExplore),
          badge: {
            txt: t("appMenu:badges.beta"),
          },
          "data-testid": "nav-rulebooks",
          // Explore is a signed-in-only page: hidden for anonymous users no matter which sidebar flag is on.
          hidden: !isCapabilityReady || !isRulebookPublicDiscoveryAllowed || isHiddenForSignedOut || (isAuthLoaded && !isKindeAuthenticated),
        },
        // Non-subscribers get Subscribe right after Lorebooks; subscribers keep it in the bottom section.
        ...(isAnonymousOrFreeUser ? [subscribeItem] : []),
      ],
      [
        {
          icon: IconVariants.SQUARE_ADD,
          label: t("appMenu:chatbots.create"),
          id: "navigation-button-create",
          hidden: !hasAnyCreationFeatures,
          isOpen: isCreateDropdownOpen,
          onOpenChange: () => {
            if (!isSideNavOpen) {
              handleCreateDropdownToggle(true);
            } else {
              handleCreateDropdownToggle(!isCreateDropdownOpen);
            }
          },
          items: [
            {
              label: t("appMenu:chatbots.chatbot"),
              id: "navigation-button-create-character",
              onClick: (e: MouseEvent<HTMLButtonElement>) => handleNavigationClick(e, ROUTES.createChatbot),
              active: isActiveRoute(ROUTES.createChatbot),
              to: localizePathname(ROUTES.createChatbot),
            },
            {
              label: t("appMenu:chatbots.lorebook"),
              id: "navigation-button-create-lorebook",
              "data-testid": "nav-create-lorebook",
              hidden: !isLorebookManageAllowed || isHiddenForSignedOut,
              onClick: (e: MouseEvent<HTMLButtonElement>) => handleNavigationClick(e, ROUTES.lorebookCreate),
              active: isActiveRoute(ROUTES.lorebookCreate),
              to: localizePathname(ROUTES.lorebookCreate),
            },
            {
              label: t("appMenu:chatbots.rulebook"),
              id: "navigation-button-create-rulebook",
              "data-testid": "nav-create-rulebook",
              badge: {
                txt: t("appMenu:badges.beta"),
              },
              hidden: !isCapabilityReady || !isRulebookManageAllowed || isHiddenForSignedOut || (isAuthLoaded && !isKindeAuthenticated),
              onClick: (e: MouseEvent<HTMLButtonElement>) => handleNavigationClick(e, ROUTES.rulebookEditor),
              active: isActiveRoute(ROUTES.rulebookEditor),
              to: localizePathname(ROUTES.rulebookEditor),
            },
            {
              label: t("appMenu:chatbots.group"),
              id: "navigation-button-create-group",
              "data-testid": "nav-create-group",
              hidden: !isGroupChatManageAllowed || isHiddenForSignedOut,
              onClick: (e: MouseEvent<HTMLButtonElement>) => handleNavigationClick(e, ROUTES.groupchatCreate),
              active: isActiveRoute(ROUTES.groupchatCreate),
              to: localizePathname(ROUTES.groupchatCreate),
            },
            ...(hasVoice
              ? [
                  {
                    label: t("appMenu:chatbots.voice"),
                    id: "navigation-button-create-voice",
                    hidden: isHiddenForSignedOut,
                    badge: {
                      txt: SubscriptionTiers.PREMIUM,
                      variant: BadgeColorVariant.GOLDEN,
                    },
                    onClick: (e: MouseEvent<HTMLButtonElement>) => {
                      if (!isAuthenticated) {
                        e.preventDefault();
                        dispatch(setIsOverlayLogin(true));
                        handleSideNavClose();
                        return;
                      }
                      if (!isTtsManageCustomVoiceAllowed) {
                        e.preventDefault();
                        setStateIsUnlockVoicesModalOpen(true);
                        if (isMobile) handleSideNavClose();
                        return;
                      }
                      handleNavigationClick(e, ROUTES.voiceCreate);
                    },
                    active: isActiveRoute(ROUTES.voiceCreate),
                    to: isTtsManageCustomVoiceAllowed ? localizePathname(ROUTES.voiceCreate) : undefined,
                  },
                ]
              : []),
          ],
        },
      ],
      ...(!isHiddenForSignedOut
        ? [
            [
              {
                icon: IconVariants.FOLDER,
                label: t("appMenu:chatbots.myCreations"),
                id: "navigation-button-creations",
                hidden: isHiddenForSignedOut,
                isOpen: isCreationsOpen,
                onOpenChange: handleCreationOpen,
                items: [
                  {
                    label: t("appMenu:chatbots.chatbots"),
                    id: "navigation-button-my-chatbots",
                    onClick: (e: MouseEvent<HTMLButtonElement>) => handleNavigationClick(e, ROUTES.chatbots),
                    active: isActiveRoute(ROUTES.chatbots),
                    to: localizePathname(ROUTES.chatbots),
                  },
                  ...(isLorebookManageAllowed
                    ? [
                        {
                          label: t("appMenu:chatbots.lorebooks"),
                          id: "navigation-button-my-lorebooks",
                          onClick: (e: MouseEvent<HTMLButtonElement>) => handleNavigationClick(e, ROUTES.myCreationLorebook),
                          active: isActiveRoute(ROUTES.myCreationLorebook),
                          to: localizePathname(ROUTES.myCreationLorebook),
                        },
                      ]
                    : []),
                  {
                    label: t("appMenu:chatbots.rulebooks"),
                    id: "navigation-button-my-rulebooks",
                    "data-testid": "nav-my-rulebooks",
                    badge: {
                      txt: t("appMenu:badges.beta"),
                    },
                    hidden: !isCapabilityReady || !isRulebookManageAllowed || (isAuthLoaded && !isKindeAuthenticated),
                    onClick: (e: MouseEvent<HTMLButtonElement>) => handleNavigationClick(e, ROUTES.myCreationRulebook),
                    active: isActiveRoute(ROUTES.myCreationRulebook),
                    to: localizePathname(ROUTES.myCreationRulebook),
                  },
                  {
                    label: t("appMenu:chatbots.groups"),
                    id: "navigation-button-my-groups",
                    hidden: !isGroupChatManageAllowed,
                    onClick: (e: MouseEvent<HTMLButtonElement>) => handleNavigationClick(e, ROUTES.groupchats),
                    active: isActiveRoute(ROUTES.groupchats),
                    to: localizePathname(ROUTES.groupchats),
                  },
                  ...(hasVoice
                    ? [
                        {
                          label: t("appMenu:chatbots.voices"),
                          id: "navigation-button-my-voices",
                          badge: {
                            txt: SubscriptionTiers.PREMIUM,
                            variant: BadgeColorVariant.GOLDEN,
                          },
                          onClick: (e: MouseEvent<HTMLButtonElement>) => {
                            if (!isAuthenticated) {
                              e.preventDefault();
                              dispatch(setIsOverlayLogin(true));
                              handleSideNavClose();
                              return;
                            }
                            if (!isTtsManageCustomVoiceAllowed) {
                              e.preventDefault();
                              setStateIsUnlockVoicesModalOpen(true);
                              if (isMobile) handleSideNavClose();
                              return;
                            }
                            handleNavigationClick(e, ROUTES.voices);
                          },
                          active: isActiveRoute(ROUTES.voices),
                          to: isTtsManageCustomVoiceAllowed ? localizePathname(ROUTES.voices) : undefined,
                        },
                      ]
                    : []),
                ],
              },
              {
                icon: IconVariants.FAVORITE,
                label: t("appMenu:chatbots.favorites"),
                hidden: isHiddenForSignedOut,
                onClick: (e: MouseEvent<HTMLButtonElement>) => handleNavigationClick(e, ROUTES.favoriteBots),
                active: isActiveRoute(ROUTES.favoriteBots) || isActiveRoute(ROUTES.favoritesLorebooks) || isActiveRoute(ROUTES.favoritesGroups),
                to: localizePathname(ROUTES.favoriteBots),
              },
              {
                icon: IconVariants.BADGE_CHECK,
                label: t("appMenu:chatbots.recommendations"),
                hidden: isHiddenForSignedOut,
                onClick: (e: MouseEvent<HTMLButtonElement>) => handleNavigationClick(e, ROUTES.recommendedBots),
                active: isActiveRoute(ROUTES.recommendedBots),
                to: localizePathname(ROUTES.recommendedBots),
              },
              {
                icon: IconVariants.VENETIAN_MASK,
                label: t("appMenu:myPersonas"),
                hidden: isHiddenForSignedOut,
                onClick: (e: MouseEvent<HTMLButtonElement>) => handleNavigationClick(e, ROUTES.personas),
                active: isActiveRoute(ROUTES.personas),
                to: localizePathname(ROUTES.personas),
              },
              {
                icon: IconVariants.TROPHY,
                label: t("appMenu:chatbots.leaderboard"),
                onClick: (e: MouseEvent<HTMLButtonElement>) => handleNavigationClick(e, ROUTES.leaderboards),
                active: isActiveRoute(ROUTES.leaderboards),
                hidden: !isSpicyChat || isHiddenForSignedOut,
                to: localizePathname(ROUTES.leaderboards),
              },
              {
                icon: IconVariants.USER_CANCEL,
                label: t("appMenu:chatbots.blockedCreators"),
                hidden: !isAuthenticated || !isBlockCreatorsAllowed,
                onClick: (e: MouseEvent<HTMLButtonElement>) => handleNavigationClick(e, ROUTES.blockedCreators),
                active: isActiveRoute(ROUTES.blockedCreators),
                to: localizePathname(ROUTES.blockedCreators),
              },
            ],
          ]
        : []),
      [
        ...(isAnonymousOrFreeUser ? [] : [subscribeItem]),
        {
          icon: IconVariants.INFO,
          label: t("appMenu:help"),
          to: customerSupportUrl!,
          target: "_blank",
        },
      ],
    ];
  }, [
    t,
    isProfileModified,
    isAuthenticated,
    isAnonymousOrFreeUser,
    isKindeAuthenticated,
    isAuthLoaded,
    currentRoute,
    hasLorebookDiscoverPublic,
    isGalleryAllowed,
    hasVoice,
    isCreateDropdownOpen,
    isCreationsOpen,
    isSideNavOpen,
    localizePathname,
    isLorebookPublicDiscoveryAllowed,
    isLorebookManageAllowed,
    isGroupChatManageAllowed,
    isTtsManageCustomVoiceAllowed,
    isBlockCreatorsAllowed,
    isRulebookManageAllowed,
    isRulebookPublicDiscoveryAllowed,
    isHiddenForSignedOut,
    isCapabilityReady,
    isDiscoveryPageAllowed,
  ]); // Only re-render when these dependencies change

  const footerItems = useMemo(
    () => ({
      button: {
        icon: IconVariants.LOGIN,
        label: isAuthenticated ? t("common:signOut") : t("common:signIn"),
        onPress: isAuthenticated ? onClickLogout : onClickLogin,
        variant: isAuthenticated ? ButtonVariant.OUTLINED : ButtonVariant.INVERSE,
      },
      socials: isSpicyChat
        ? [
            {
              to: SPICYCHAT_DISCORD,
              target: "_blank",
              icon: <DiscordThemeIcon />,
              onClick: () => handleSocialMediaClick(SOCIAL_MEDIA_EVENT_VALUE.Discord),
              label: SOCIAL_MEDIA_EVENT_VALUE.Discord,
            },
            {
              to: SPICYCHAT_TWITTER,
              target: "_blank",
              icon: <TwitterThemeIcon />,
              onClick: () => handleSocialMediaClick(SOCIAL_MEDIA_EVENT_VALUE.X),
              label: SOCIAL_MEDIA_EVENT_VALUE.X,
            },
            {
              to: SPICYCHAT_REDDIT,
              target: "_blank",
              icon: <RedditThemeIcon />,
              onClick: () => handleSocialMediaClick(SOCIAL_MEDIA_EVENT_VALUE.Reddit),
              label: SOCIAL_MEDIA_EVENT_VALUE.Reddit,
            },
          ]
        : [],
      legals: [
        {
          label: t("appMenu:footer.terms"),
          id: "navigation-link-terms",
          to: localizePathname(tcUrl || ""),
          children: t("appMenu:footer.terms"),
        },
        {
          label: t("appMenu:footer.privacy"),
          to: localizePathname(privacyPolicyUrl || ""),
          onPress: handleSideNavClose,
        },
        {
          label: t("appMenu:footer.refunds"),
          to: localizePathname(refundPolicyUrl || ""),
          onPress: handleSideNavClose,
        },
        {
          label: t("appMenu:footer.reporting"),
          to: localizePathname(ROUTES.report),
          onPress: handleSideNavClose,
        },
        {
          label: t("appMenu:footer.guidelines"),
          to: EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.guidelines,
          target: "_blank",
          onPress: handleSideNavClose,
        },
        {
          label: t("appMenu:footer.support"),
          to: customerSupportUrl || "",
          target: "_blank",
          onPress: handleSideNavClose,
        },
        {
          label: t("appMenu:footer.affiliates"),
          to: EXTERNAL_ROUTES_FOR_APPLICATION[APPLICATIONS_ID.SpicyChat].affiliates!,
          onPress: handleSideNavClose,
          hidden: !isSpicyChat,
        },
      ],
      apps: applicationBadges,
      versionLabel: `Web version: ${basicAppConfig.appVersion}-jorge-migration`,
    }),
    [t, isAuthenticated, isDark, localizePathname, applicationBadges],
  ); // Only re-render when these dependencies change

  return (
    <>
      <div>
        <NavigationUI
          open={isSideNavOpen}
          onOpenChange={handleSideNavToggle}
          logo={
            <LocalizedLink onClick={handleLogoClick} to={isProfileModified ? "#" : "/"} aria-label="home">
              <SiteLogo />
            </LocalizedLink>
          }
          items={menuItems}
          footerItems={footerItems}
        />
      </div>

      {isOverlayLogin ? <OverlayLogin onClose={onOverlayLoginClose} /> : null}
      <UnlockCustomVoicesModal isOpen={stateIsUnlockVoicesModalOpen} onClose={setStateIsUnlockVoicesModalOpen} />
    </>
  );
}

export default Navigation;
